import { ActivityIndicator, Icon, IconButton } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import type { AudioPlayerState } from "./index";

const ICON_BUTTON_SIZE = 24;
const ICON_BUTTON_WIDTH = 40;

const getIconAndA11yLabel = (
	state: AudioPlayerState,
	isEnded: boolean,
): { icon: string; a11yLabel: string } => {
	switch (state) {
		case "inProgress":
			return { icon: "pause", a11yLabel: "Pause audio" };
		case "playOnce":
			return isEnded
				? { icon: "check-circle", a11yLabel: "Audio played" }
				: { icon: "volume-high", a11yLabel: "Listen once" };
		case "played":
			return { icon: "replay", a11yLabel: "Replay audio" };
		case "locked":
			return { icon: "lock", a11yLabel: "Locked" };
		case "disabled":
			return { icon: "play", a11yLabel: "Disabled" };
		default:
			return { icon: "play", a11yLabel: "Play audio" };
	}
};

type Props = {
	state: AudioPlayerState;
	isEnded: boolean;
	isPlaying: boolean;
	isLoading: boolean;
	onPress: () => void;
	playOnceInteractive?: boolean;
};

const PlayerIconButton = ({
	state,
	isEnded,
	isPlaying,
	isLoading,
	onPress,
	playOnceInteractive,
}: Props) => {
	const theme = useAppTheme();
	const { icon, a11yLabel } = getIconAndA11yLabel(state, isEnded);
	const canStartPlayOnce =
		Boolean(playOnceInteractive) && !isPlaying && !isEnded;
	const isDecorative =
		state === "locked" || (state === "playOnce" && !canStartPlayOnce);
	const isDisabled = state === "disabled";

	if (isDecorative) {
		const showBuffering = isLoading && state !== "locked" && !isEnded;

		return (
			<Box
				width={ICON_BUTTON_WIDTH}
				height={ICON_BUTTON_WIDTH}
				borderRadius={ICON_BUTTON_WIDTH / 2}
				backgroundColor={theme.colors.primary}
				alignItems="center"
				justifyContent="center"
				accessibilityLabel={showBuffering ? "Loading audio" : a11yLabel}
			>
				{showBuffering ? (
					<ActivityIndicator
						size={ICON_BUTTON_SIZE}
						color={theme.colors.onPrimary}
					/>
				) : (
					<Icon
						source={icon}
						size={ICON_BUTTON_SIZE}
						color={theme.colors.onPrimary}
					/>
				)}
			</Box>
		);
	}

	return (
		<IconButton
			icon={icon}
			loading={isLoading}
			size={ICON_BUTTON_SIZE}
			onPress={isDisabled ? () => undefined : onPress}
			disabled={isDisabled}
			containerColor={
				isDisabled ? theme.colors.surfaceDisabled : theme.colors.primary
			}
			iconColor={
				isDisabled ? theme.colors.onSurfaceDisabled : theme.colors.onPrimary
			}
			accessibilityLabel={a11yLabel}
			style={{ margin: 0, width: ICON_BUTTON_WIDTH }}
		/>
	);
};

export default PlayerIconButton;
